// ============================================================
// playbackEngine  — chord-level cursor + repeat support
// ============================================================

import * as Tone from 'tone';
import type { Song, Section, Bar, Chord, Key } from './types';

// ── Types ─────────────────────────────────────────────────────

export type PlaybackState = 'playing' | 'stopped';

export interface PlaybackCursor {
  sectionIdx: number;
  barIdx:     number;
  chordIdx:   number;
}

type CursorCallback = (cursor: PlaybackCursor | null) => void;
type StateCallback  = (state: PlaybackState) => void;

interface LoadOptions {
  showClick?:   boolean;
  clickVolume?: number;  // 0–1 linear gain
}

interface PlaylistEntry {
  sectionIdx: number;
  barIdx:     number;
  bar:        Bar;
  section:    Section;
}

// ── Note/chord helpers ────────────────────────────────────────

const NOTE_NAMES = ['C','C#','D','D#','E','F','F#','G','G#','A','A#','B'] as const;
const DEGREE_SEMITONES = [0, 2, 4, 5, 7, 9, 11];

const ROOT_SEMITONES: Record<string, number> = {
  C: 0, 'C#': 1, Db: 1, D: 2, 'D#': 3, Eb: 3, E: 4,
  F: 5, 'F#': 6, Gb: 6, G: 7, 'G#': 8, Ab: 8, A: 9,
  'A#': 10, Bb: 10, B: 11,
};

const QUALITY_INTERVALS: Partial<Record<string, number[]>> = {
  maj:   [0, 4, 7],   major: [0, 4, 7],
  min:   [0, 3, 7],   minor: [0, 3, 7],
  dom7:  [0, 4, 7, 10],
  maj7:  [0, 4, 7, 11],
  min7:  [0, 3, 7, 10],
  dim:   [0, 3, 6],
  dim7:  [0, 3, 6, 9],
  hdim7: [0, 3, 6, 10],
  aug:   [0, 4, 8],
  sus2:  [0, 2, 7],
  sus4:  [0, 5, 7],
  power: [0, 7],
};

function chordToNotes(chord: Chord, key: Key | null): string[] {
  if (!key) return [];
  const rootSemitone = ROOT_SEMITONES[key.root] ?? 0;
  const degreeIdx    = ((chord.degree - 1) % 7 + 7) % 7;
  let   chordRoot    = (rootSemitone + DEGREE_SEMITONES[degreeIdx]) % 12;
  if (chord.accidental === 'sharp') chordRoot = (chordRoot + 1)  % 12;
  if (chord.accidental === 'flat')  chordRoot = (chordRoot + 11) % 12;
  const intervals = QUALITY_INTERVALS[chord.quality] ?? [0, 4, 7];
  return intervals.map((interval) => {
    const pitch = chordRoot + interval;
    return `${NOTE_NAMES[pitch % 12]}${4 + Math.floor(pitch / 12)}`;
  });
}

function parseBpm(tempo: string | null | undefined): number {
  if (!tempo) return 120;
  const match = tempo.match(/(\d+)/);
  return match ? parseInt(match[1], 10) : 120;
}

// ── Playlist builder ──────────────────────────────────────────

function buildPlaylist(song: Song): PlaylistEntry[] {
  const playlist: PlaylistEntry[] = [];

  song.sections.forEach((section, sectionIdx) => {
    const bars = section.bars;
    let i = 0;

    while (i < bars.length) {
      const bar = bars[i];

      if (bar.repeatStart) {
        let endIdx = i;
        for (let k = i + 1; k < bars.length; k++) {
          if (bars[k].repeatEnd !== null) { endIdx = k; break; }
        }
        const repeatCount = bars[endIdx].repeatEnd ?? 2;
        for (let pass = 0; pass < repeatCount; pass++) {
          for (let j = i; j <= endIdx; j++) {
            playlist.push({ sectionIdx, barIdx: j, bar: bars[j], section });
          }
        }
        i = endIdx + 1;
      } else {
        playlist.push({ sectionIdx, barIdx: i, bar, section });
        i++;
      }
    }
  });

  return playlist;
}

// ── Synth ─────────────────────────────────────────────────────

let _synth: Tone.PolySynth | null = null;

function getSynth(): Tone.PolySynth {
  if (!_synth) {
    _synth = new Tone.PolySynth(Tone.Synth, {
      oscillator: { type: 'triangle' },
      envelope:   { attack: 0.02, decay: 0.3, sustain: 0.4, release: 1.2 },
    }).toDestination();
  }
  return _synth;
}

// ── Click sound — woodblock/drumstick style ───────────────────

function triggerClick(
  time: number,
  isDownbeat: boolean,
  gainNode: Tone.Gain,
): void {
  const context  = Tone.getContext().rawContext as AudioContext;
  const duration = 0.018;

  const bufferSize = Math.ceil(context.sampleRate * duration);
  const buffer     = context.createBuffer(1, bufferSize, context.sampleRate);
  const data       = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) {
    data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / bufferSize, 3);
  }
  const source  = context.createBufferSource();
  source.buffer = buffer;

  const filter           = context.createBiquadFilter();
  filter.type            = 'bandpass';
  filter.frequency.value = isDownbeat ? 1400 : 2200;
  filter.Q.value         = isDownbeat ? 1.2  : 1.8;

  const gain      = context.createGain();
  gain.gain.value = isDownbeat ? 1.0 : 0.65;

  source.connect(filter);
  filter.connect(gain);
  gain.connect(gainNode.input as AudioNode);

  const startTime = typeof time === 'number' ? time : context.currentTime;
  source.start(startTime);
  source.stop(startTime + duration + 0.01);
}

// ── Engine ────────────────────────────────────────────────────

class PlaybackEngine {
  private initialized  = false;
  private _playing     = false;
  private _volume      = -12;   // chord synth volume in dB
  private _clickVolume = 0.8;   // click gain 0–1 (default loud enough to hear)

  private cursorCb: CursorCallback | null = null;
  private stateCb:  StateCallback  | null = null;

  private part:      Tone.Part | null = null;
  private clickPart: Tone.Part | null = null;
  private clickGain: Tone.Gain | null = null;

  addCursorCallback(cb: CursorCallback): () => void {
    this.cursorCb = cb;
    return () => { if (this.cursorCb === cb) this.cursorCb = null; };
  }

  addStateCallback(cb: StateCallback): () => void {
    this.stateCb = cb;
    return () => { if (this.stateCb === cb) this.stateCb = null; };
  }

  get isPlaying() { return this._playing; }

  // ── Load ──────────────────────────────────────────────────────

  async load(song: Song, options: LoadOptions = {}): Promise<void> {
    await Tone.start();
    this.stop();

    // Use provided clickVolume if supplied, otherwise keep current setting
    if (options.clickVolume !== undefined) {
      this._clickVolume = options.clickVolume;
    }

    const bpm            = parseBpm(song.tempo);
    const beatsPerBar    = song.timeSignature?.numerator ?? 4;
    const secondsPerBeat = 60 / bpm;

    Tone.getTransport().bpm.value = bpm;
    Tone.getTransport().cancel();

    const playlist = buildPlaylist(song);

    const chordEvents: Array<{ time: number; notes: string[]; duration: number }> = [];
    const cursorEvents: Array<{ time: number; sectionIdx: number; barIdx: number; chordIdx: number }> = [];
    const clickEvents: Array<{ time: number; isDownbeat: boolean }> = [];

    let absoluteBeat = 0;

    playlist.forEach(({ sectionIdx, barIdx, bar, section }) => {
      const tsNumerator  = bar.timeSignature?.numerator ?? beatsPerBar;
      const barStartSecs = absoluteBeat * secondsPerBeat;

      for (let beat = 0; beat < tsNumerator; beat++) {
        clickEvents.push({
          time:       (absoluteBeat + beat) * secondsPerBeat,
          isDownbeat: beat === 0,
        });
      }

      const effectiveKey = section.key ?? song.defaultKey;

      if (bar.chords.length === 0) {
        cursorEvents.push({ time: barStartSecs, sectionIdx, barIdx, chordIdx: 0 });
      } else {
        bar.chords.forEach((chord, ci) => {
          if (chord.isEmpty) return;
          const chordStartBeat = absoluteBeat + (chord.position ?? 0);
          const chordStartSecs = chordStartBeat * secondsPerBeat;
          const nextPosition   = bar.chords[ci + 1]?.position ?? tsNumerator;
          const durationSecs   = (nextPosition - (chord.position ?? 0)) * secondsPerBeat;
          const notes          = chordToNotes(chord, effectiveKey);

          cursorEvents.push({ time: chordStartSecs, sectionIdx, barIdx, chordIdx: ci });

          if (notes.length > 0) {
            chordEvents.push({
              time:     chordStartSecs,
              notes,
              duration: Math.max(0.05, durationSecs - 0.05),
            });
          }
        });
      }

      absoluteBeat += tsNumerator;
    });

    const totalSecs = absoluteBeat * secondsPerBeat;
    const synth     = getSynth();
    synth.volume.value = this._volume;

    if (chordEvents.length > 0) {
      this.part = new Tone.Part<{ time: number; notes: string[]; duration: number }>(
        (time, ev) => { synth.triggerAttackRelease(ev.notes, ev.duration, time); },
        chordEvents,
      );
      this.part.start(0);
    }

    cursorEvents.forEach(({ time, sectionIdx, barIdx, chordIdx }) => {
      Tone.getTransport().schedule((t) => {
        Tone.getDraw().schedule(() => {
          this.cursorCb?.({ sectionIdx, barIdx, chordIdx });
        }, t);
      }, time);
    });

    if (options.showClick && clickEvents.length > 0) {
      this.clickGain = new Tone.Gain(this._clickVolume).toDestination();
      const gainNode = this.clickGain;

      this.clickPart = new Tone.Part<{ time: number; isDownbeat: boolean }>(
        (time, ev) => { triggerClick(time, ev.isDownbeat, gainNode); },
        clickEvents,
      );
      this.clickPart.start(0);
    }

    Tone.getTransport().schedule((time) => {
      Tone.getDraw().schedule(() => {
        this._playing = false;
        this.cursorCb?.(null);
        this.stateCb?.('stopped');
        Tone.getTransport().stop();
        Tone.getTransport().position = 0;
      }, time);
    }, totalSecs);

    this.initialized = true;
  }

  // ── Play / Stop ───────────────────────────────────────────────

  play(): void {
    if (!this.initialized) return;
    this._playing = true;
    this.stateCb?.('playing');
    Tone.getTransport().start();
  }

  stop(): void {
    Tone.getTransport().stop();
    Tone.getTransport().cancel();
    Tone.getTransport().position = 0;
    this.part?.dispose();
    this.clickPart?.dispose();
    this.clickGain?.dispose();
    this.part      = null;
    this.clickPart = null;
    this.clickGain = null;
    _synth?.releaseAll();
    this._playing    = false;
    this.initialized = false;
    this.cursorCb?.(null);
    this.stateCb?.('stopped');
  }

  // ── Volume controls ───────────────────────────────────────────

  /** Chord synth volume in dB (e.g. -20 to 0). */
  setVolume(db: number): void {
    this._volume = db;
    if (_synth) _synth.volume.value = db;
  }

  /** Click volume as 0–1 linear gain. Updates live if click is active. */
  setClickVolume(gain: number): void {
    this._clickVolume = gain;
    if (this.clickGain) this.clickGain.gain.value = gain;
  }

  dispose(): void {
    this.stop();
    _synth?.dispose();
    _synth = null;
  }
}

export const playbackEngine = new PlaybackEngine();